import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, Package, ShoppingCart, BarChart3, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STEPS = [
  { id: 'connect', label: 'Conectando con tu cuenta...', icon: Sparkles },
  { id: 'products', label: 'Importando inventario activo...', icon: Package },
  { id: 'orders', label: 'Descargando ventas recientes...', icon: ShoppingCart },
  { id: 'metrics', label: 'Calculando métricas iniciales...', icon: BarChart3 },
];

const OnboardingLoader = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const site = searchParams.get('site');
  
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [status, setStatus] = useState('loading'); // loading, completed, error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Guardar token
    localStorage.setItem('authToken', token);

    // Iniciar polling del progreso
    const pollProgress = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = response.data;
        setProgress(data.progress || 0);
        setCurrentStep(data.step || '');
        
        if (data.status === 'completed') {
          setStatus('completed');
          // Esperar un momento para mostrar el 100% y luego redirigir
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
          return true; // Stop polling
        } else if (data.status === 'error') {
          setStatus('error');
          setError(data.step || 'Error desconocido');
          return true; // Stop polling
        } else if (data.status === 'not_started') {
          // El sync aún no ha empezado, seguir esperando
          return false;
        }
        
        return false; // Continue polling
      } catch (err) {
        console.error('Error polling status:', err);
        // Si es 404 o similar, puede que el sync no haya empezado
        return false;
      }
    };

    // Polling cada 1 segundo
    const interval = setInterval(async () => {
      const shouldStop = await pollProgress();
      if (shouldStop) {
        clearInterval(interval);
      }
    }, 1000);

    // Primera llamada inmediata
    pollProgress();

    // Cleanup
    return () => clearInterval(interval);
  }, [token, navigate]);

  // Determinar qué paso está activo basado en el progreso
  const getStepStatus = (stepIndex) => {
    const thresholds = [5, 25, 50, 80];
    if (progress >= thresholds[stepIndex]) {
      if (stepIndex === STEPS.length - 1 && status === 'completed') {
        return 'completed';
      }
      if (progress >= (thresholds[stepIndex + 1] || 100)) {
        return 'completed';
      }
      return 'active';
    }
    return 'pending';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0a0a20] to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4 relative">
            {status === 'completed' ? (
              <CheckCircle className="w-10 h-10 text-green-400" />
            ) : status === 'error' ? (
              <AlertCircle className="w-10 h-10 text-red-400" />
            ) : (
              <>
                <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
                <div className="absolute inset-0 rounded-full border-2 border-blue-500/30 animate-pulse"></div>
              </>
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">
            {status === 'completed' ? '¡Todo listo!' : 
             status === 'error' ? 'Hubo un problema' : 
             'Preparando tu cuenta'}
          </h1>
          
          <p className="text-slate-400">
            {status === 'completed' ? 'Redirigiendo al dashboard...' :
             status === 'error' ? error :
             'Esto solo toma unos segundos'}
          </p>
        </div>

        {/* Progress Card */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Progreso</span>
              <span className="text-blue-400 font-medium">{progress}%</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            {STEPS.map((step, index) => {
              const stepStatus = getStepStatus(index);
              const Icon = step.icon;
              
              return (
                <div 
                  key={step.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                    stepStatus === 'active' ? 'bg-blue-500/10 border border-blue-500/30' :
                    stepStatus === 'completed' ? 'bg-green-500/10' :
                    'bg-slate-800/30'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    stepStatus === 'completed' ? 'bg-green-500/20' :
                    stepStatus === 'active' ? 'bg-blue-500/20' :
                    'bg-slate-700/50'
                  }`}>
                    {stepStatus === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : stepStatus === 'active' ? (
                      <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                    ) : (
                      <Icon className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                  
                  <span className={`text-sm ${
                    stepStatus === 'completed' ? 'text-green-400' :
                    stepStatus === 'active' ? 'text-white' :
                    'text-slate-500'
                  }`}>
                    {stepStatus === 'active' && currentStep ? currentStep : step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Current Action */}
          {status === 'loading' && currentStep && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <p className="text-xs text-slate-500 text-center animate-pulse">
                {currentStep}
              </p>
            </div>
          )}
        </div>

        {/* Error Action */}
        {status === 'error' && (
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/connect-meli')}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              Intentar nuevamente
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-6">
          No cierres esta ventana
        </p>
      </div>
    </div>
  );
};

export default OnboardingLoader;
